import React, { useEffect, useState } from "react";
import { apiFetch, apiFetchFormData } from "../utils/wkCommon";
import { isZh } from "../utils/language";
import { getUser } from "../utils/auth";

const user = getUser();

export default function SongFileForm({ song, onClose, canEdit, onShare }) {

  const zh = isZh();

  const [rows, setRows] = useState([]);
  const [mode, setMode] = useState("list"); // list | form
  const [editing, setEditing] = useState(null);
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);

  const emptyForm = {
    filetype: "",
    mediaUrl: "",
    mediaFile: null,
    filename: "",
    romanization: false,
    translation: false,
    filesize: "",
    versionNotes: ""
  };

  const [form, setForm] = useState(emptyForm);

  const fieldStyle = { marginBottom: "0.75rem" };

  const labelTextStyle = {
    fontSize: "0.75rem",
    color: "#666",
    marginBottom: "0.25rem"
  };

  const inputStyle = {
    width: "95%",
    padding: "0.625rem 0.875rem",
    borderRadius: "0.625rem",
    border: "1px solid #ddd",
    fontSize: "0.875rem"
  };

  const iconBtnStyle = {
    width: "32px",
    height: "32px",
    flex: "0 0 calc(33.33% - 0.25rem)", 
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    fontSize: "1rem",
    cursor: "pointer",
    border: "1px solid #ddd",
    borderRadius: "0.5rem",
    background: "#fff"
  };

  const dangerBtnStyle = {
    ...iconBtnStyle,
    background: "#e53935",
    borderColor: "#d32f2f",
    color: "#fff"
  };

  // ================= LOAD =================
  async function load() {
    setLoading(true);

    try {
      const params = new URLSearchParams({
        "filters[virtuous_song][documentId][$eq]": song.documentId,
        "populate[0]": "mediaFile"
      });

      const json = await apiFetch(`/api/song-files?${params}`);
      setRows(json?.data || []);
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    load();
  }, []);

  // ================= HELPERS =================
  function formatSize(bytes) {
    if (!bytes) return "-";
    if (bytes < 1024) return bytes + " B";
    if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + " KB";
    return (bytes / (1024 * 1024)).toFixed(1) + " MB";
  }

  function detectFileMeta(file) {
    const type = file.type || "";
    const ext = file.name.split(".").pop().toLowerCase();

    let filetype = "Other";

    if (type.includes("audio")) filetype = "Audio";
    else if (type.includes("video")) filetype = "Video";
    else if (type.includes("presentation") || ["ppt", "pptx"].includes(ext)) {
      filetype = "Slides";
    }
    else if (
      type.includes("pdf") ||
      type.includes("msword") ||
      type.includes("word") ||
      type.includes("text")
    ) {
      filetype = "Text";
    }

    return {
      filetype,
      filesize: file.size,
      filename: file.name
    };
  }

  function handleDrop(file) {
    const meta = detectFileMeta(file);

    setForm(f => ({
      ...f,
      mediaFile: file,
      mediaUrl: "",
      ...meta
    }));
  }

  function openUrl(url) {
    window.open(url, "_blank");
  }

  function downloadFile(url, filename) {
    const a = document.createElement("a");
    a.href = url;
    a.download = filename || "file";
    a.target = "_blank";
    a.click();
  }

  function isYoutube(url) {
    return url && (
      url.includes("youtube.com") ||
      url.includes("youtu.be")
    );
  }

  function extractYoutubeId(url) {
    try {
      const u = new URL(url);

      if (u.hostname.includes("youtu.be")) {
        return u.pathname.slice(1);
      }

      return u.searchParams.get("v");
    } catch {
      return "";
    }
  }

  // ================= UPLOAD =================
  // OLD FETCH W/O PROGRESS BAR
  /*
  async function uploadFile(file) {
    const fd = new FormData();
    fd.append("files", file);

    const res = await apiFetchFormData("/api/upload", fd, {
      method: "POST"
    });

    const arr = Array.isArray(res) ? res : res?.data || [];
    if (!arr.length) throw new Error("Upload failed");

    return arr[0];
  }
    */

  function uploadFile(file) {
    return new Promise((resolve, reject) => {
      const xhr = new XMLHttpRequest();

      xhr.open("POST", `${process.env.REACT_APP_BACKEND_URL}/api/upload`);

      // JWT header (WAJIB, sesuai project rules)
      const token = localStorage.getItem("lefy_auth");
      if (token) {
        const parsed = JSON.parse(token);
        if (parsed?.jwt) {
          xhr.setRequestHeader("Authorization", `Bearer ${parsed.jwt}`);
        }
      }

      xhr.upload.onprogress = (e) => {
        if (e.lengthComputable) {
          const percent = Math.round((e.loaded * 100) / e.total);
          setUploadProgress(percent);
        }
      };

      xhr.onload = () => {
        setUploadProgress(0);

        if (xhr.status >= 200 && xhr.status < 300) {
          const res = JSON.parse(xhr.responseText);
          const arr = Array.isArray(res) ? res : res?.data || [];
          resolve(arr[0]);
        } else {
          reject(new Error("Upload failed"));
        }
      };

      xhr.onerror = () => {
        setUploadProgress(0);
        reject(new Error("Upload error"));
      };

      const fd = new FormData();
      fd.append("files", file);

      xhr.send(fd);
    });
  }

  // ================= SAVE =================
  async function handleSave() {
    if (saving) return;

    const hasExistingFile = editing?.mediaFile?.length;

    if (!form.mediaFile && !form.mediaUrl && !hasExistingFile) {
      alert("Upload file atau isi URL");
      return;
    }

    if (form.mediaFile && form.mediaUrl) {
      alert(zh
        ? "只能選擇檔案或連結其中一個"
        : "Choose either file or URL, not both");
      return;
    }

    try {
      setSaving(true);

      let uploaded = null;

      const isNewFile = form.mediaFile instanceof File;

      if (isNewFile) {
        uploaded = await uploadFile(form.mediaFile);
      }

      const payload = {
        filetype: form.filetype,
        mediaUrl: isNewFile ? null : form.mediaUrl,
        romanization: form.romanization,
        translation: form.translation,
        filesize: form.mediaFile ? form.filesize : null,
        versionNotes: form.versionNotes,
        virtuous_song: song.documentId,
        ...(uploaded
          ? { mediaFile: [uploaded.id] }
          : editing?.mediaFile?.length
            ? { mediaFile: editing.mediaFile.map(f => f.id) }
            : {}),
        ...(editing
          ? { recordEditor: user?.username }
          : {
              recordOwner: user?.username,
              recordEditor: user?.username
            })
      };

      if (editing) {
        await apiFetch(`/api/song-files/${editing.documentId}`, {
          method: "PUT",
          body: { data: payload }
        });
      } else {
        await apiFetch(`/api/song-files`, {
          method: "POST",
          body: { data: payload }
        });
      }

      setForm(emptyForm);
      setEditing(null);
      setMode("list");
      load();

    } catch (err) {
      console.error(err);
      alert(err.message);
    } finally {
      setSaving(false);
    }
  }

  function startAdd() {
    setForm(emptyForm);
    setEditing(null);
    setMode("form");
  }

  function startEdit(item) {
    setEditing(item);

    setForm({
      filetype: item.filetype || "",
      mediaUrl: item.mediaUrl || "",
      mediaFile: item.mediaFile || null,
      filename: item.mediaFile?.[0]?.name || "",
      romanization: item.romanization || false,
      translation: item.translation || false,
      filesize: item.filesize || "",
      versionNotes: item.versionNotes || ""
    });

    setMode("form");
  }

  async function remove(item) {
    if (!window.confirm("Delete file?")) return;

    await apiFetch(`/api/song-files/${item.documentId}`, {
      method: "DELETE"
    });

    load();
  }

  // ================= UI =================
  if (mode === "list") {
    return (
      <>
        {/* LIST */}
        {loading ? (
          <div style={{ color: "#999", fontSize: "0.9rem" }}>
            {zh ? "載入中..." : "Loading..."}
          </div>
        ) : rows.length === 0 ? (
          <div style={{ color: "#999", fontSize: "0.9rem" }}>
            {zh ? "沒有資料" : "No data"}
          </div>
        ) : (
          rows.map(r => {
            const file = r.mediaFile?.[0];

            return (
              <div
                  className="content-row"
                  style={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "flex-start",
                    gap: "0.75rem"
                  }}
                >
                <div style={{ fontSize: "0.85rem" }}>
                  <div style={{ display: "flex", alignItems: "center", gap: "0.5rem" }}>
                    <b>
                      {r.filetype} { " | " }
                      {r.mediaFile?.length
                        ? (zh ? " 檔案" : " File")
                        : (zh ? " 連結" : " URL")}
                      <span style={{ color: "#999", fontSize:"0.8rem", paddingLeft:"0.5rem" }}>{formatSize(r.filesize)}</span>
                    </b>
                  </div>

                  <div style={{
                      wordBreak: "break-all",
                      maxWidth: "100%"
                    }}>
                    {file?.name || r.mediaUrl || "-"}
                  </div>

                  <div style={{ display: "flex", marginTop: "0.125rem" }}>
                    {r.romanization && (
                      <>
                      <span style={{ color: "green", fontSize: "0.8rem", paddingRight: "0.5rem" }}>✔ </span>
                      <span style={{ color: "#666", fontSize: "0.8rem", paddingRight: "1.5rem" }}>
                        拼音 Pinyin
                      </span>
                      </>
                    )}

                    {r.translation && (
                      <>
                      <span style={{ color: "green", fontSize: "0.8rem", paddingRight: "0.5rem" }}>✔ </span>
                      <span style={{ color: "#666", fontSize: "0.8rem" }}>
                        翻譯 Terjemahan
                      </span>
                      </>
                    )}
                  </div>

                  <div style={{ color: "#999" }}>
                    {r.versionNotes}
                  </div>

                </div>

                <div style={{
                  display: "grid",
                  gridTemplateColumns: "repeat(2, 1fr)",
                  gap: "0.25rem",
                  width: "72px"
                  }}>

                  { canEdit && (
                    <>
                  {/* EDIT */}
                  <button
                    style={iconBtnStyle}
                    title={zh ? "編輯" : "Edit"}
                    onClick={() => startEdit(r)}
                  >
                    ✏️
                  </button>

                  {/* DELETE */}
                  <button
                    style={dangerBtnStyle}
                    title={zh ? "刪除" : "Delete"}
                    onClick={() => remove(r)}
                    onMouseOver={e => {
                      e.currentTarget.style.background = "#c62828";
                      e.currentTarget.style.transform = "scale(1.05)";
                    }}
                    onMouseOut={e => {
                      e.currentTarget.style.background = "#e53935";
                      e.currentTarget.style.transform = "scale(1)";
                    }}
                  >
                    🗑
                  </button>
                  </>
                  )}
                  
                  {canEdit && onShare && (
                    <button
                      style={iconBtnStyle}
                      title="Share"
                      onClick={() => onShare(r)}
                    >
                      🔗
                    </button>
                  )}

                  {/* DOWNLOAD FILE */}
                  {r.mediaFile?.length && r.mediaFile[0]?.url && (
                    <button
                      style={iconBtnStyle}
                      title={zh ? "下載檔案" : "Download"}
                      onClick={() =>
                        downloadFile(r.mediaFile[0].url, r.mediaFile[0].name)
                      }
                    >
                      ⬇️
                    </button>
                  )}

                  {/* YOUTUBE DOWNLOAD */}
                  {isYoutube(r.mediaUrl) && (
                    <button
                      style={iconBtnStyle}
                      title={zh ? "下載 YouTube" : "Download YouTube"}
                      onClick={() =>
                        openUrl(`https://ssyoutube.com/watch?v=${extractYoutubeId(r.mediaUrl)}`)
                      }
                    >
                      ⬇️
                    </button>
                  )}

                  {/* OPEN URL */}
                  {r.mediaUrl && (
                    <button
                      style={iconBtnStyle}
                      title={zh ? "開啟連結" : "Open"}
                      onClick={() => openUrl(r.mediaUrl)}
                    >
                      ▶️
                    </button>
                  )}
                </div>
              </div>
            );
          })
        )}

        <div style={{ display: "flex", justifyContent: "space-between", marginTop: "0.75rem" }}>
          {canEdit && (
            <button className="btn-primary" disabled={!canEdit} onClick={startAdd}>
              + {zh ? "新增" : "Add"}
            </button>
          )}

          <button className="btn-secondary" onClick={onClose}>
            {zh ? "關閉" : "Close"}
          </button>
        </div>
      </>
    );
  }

  // ================= FORM =================
  return (
    <>
      <div style={fieldStyle}>
        <div style={labelTextStyle}>
          {zh ? "檔案類型" : "File Type"}
        </div>

        <select
          style={inputStyle}
          value={form.filetype}
          onChange={e => setForm({ ...form, filetype: e.target.value })}
        >
          <option value="">-- Select --</option>
          <option>Audio</option>
          <option>Video</option>
          <option>Slides</option>
          <option>Text</option>
          <option>Other</option>
        </select>
      </div>

      {/* DROP */}
      <div
        style={{
          border: "1px dashed #ccc",
          padding: "1rem",
          textAlign: "center",
          marginBottom: 8,
          cursor: "pointer",
          opacity: form.mediaUrl ? 0.5 : 1,
          pointerEvents: form.mediaUrl ? "none" : "auto"          
        }}
        onDragOver={e => e.preventDefault()}
        onDrop={e => {
          e.preventDefault();
          const file = e.dataTransfer.files[0];
          if (file) handleDrop(file);
        }}
        onClick={() => document.getElementById("fileInput").click()}
      >
        {form.filename || (zh ? "拖曳或點擊上傳" : "Drag or click")}
      </div>

      <input
        id="fileInput"
        type="file"
        hidden
        onChange={e => handleDrop(e.target.files[0])}
      />

      {/* URL */}
      <div style={fieldStyle}>
        <div style={labelTextStyle}>
          {zh ? "媒體連結" : "Media URL"}
        </div>

        <input
          style={inputStyle}
          disabled={!!form.mediaFile?.length}
          value={form.mediaUrl}
          onChange={e =>
            setForm({
              ...form,
              mediaUrl: e.target.value,
              mediaFile: null
            })
          }
        />
      </div>

      {/* CHECKBOX */}
      <div style={{ marginTop: 0 }}>
        <label>
          <input type="checkbox"
            checked={form.romanization}
            onChange={e => setForm({ ...form, romanization: e.target.checked })}
          /> Romanization (拼音 / cara baca)
        </label>

        <br />

        <label>
          <input type="checkbox"
            checked={form.translation}
            onChange={e => setForm({ ...form, translation: e.target.checked })}
          /> Translation (翻譯 / arti)
        </label>
      </div>

      {/* NOTES */}
      <div style={fieldStyle}>
        <div style={labelTextStyle}>
          {zh ? "版本備註" : "Version Notes"}
        </div>

        <input
          style={inputStyle}
          value={form.versionNotes}
          onChange={e => setForm({ ...form, versionNotes: e.target.value })}
        />
      </div>

      {/* ACTION */}
      <div style={{ marginTop: 10, display: "flex", gap: 6 }}>
        <button
          className="btn-secondary"
          onClick={() => setMode("list")}
          disabled={saving}
        >
          {zh ? "取消" : "Cancel"}
        </button>

        <button className="btn-primary" onClick={handleSave} disabled={!canEdit || saving}>
          {saving ? (zh ? "處理中..." : "Processing...") : (editing ? "Update" : "Add")}
        </button>
      </div>

      {uploadProgress > 0 && (
        <div style={{
          position: "fixed",
          bottom: "1rem",
          right: "1rem",
          background: "#333",
          color: "#fff",
          padding: "0.5rem 0.75rem",
          borderRadius: "0.5rem",
          fontSize: "0.8rem",
          boxShadow: "0 2px 8px rgba(0,0,0,0.2)"
        }}>
          Uploading... {uploadProgress}%
        </div>
      )}
    </>
  );
}